<template>
  <div class="diffraction-limit-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>θ ≈ 1.22 λ / D</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Two Point Sources -->
          <g transform="translate(50, 120)">
             <!-- Airy Disks visualization -->
             <g :transform="`translate(0, -${spacing/2})`">
                <circle r="3" fill="#fff" />
                <circle v-for="i in 3" :key="'w1'+i" r="0" fill="none" stroke="#fff" :stroke-width="4-i">
                   <animate attributeName="r" :from="0" :to="airyRadius * i" dur="2s" repeatCount="indefinite" />
                   <animate attributeName="opacity" values="0.6;0" dur="2s" repeatCount="indefinite" />
                </circle>
             </g>
             <g :transform="`translate(0, ${spacing/2})`">
                <circle r="3" fill="#fff" />
                <circle v-for="i in 3" :key="'w2'+i" r="0" fill="none" stroke="#fff" :stroke-width="4-i">
                   <animate attributeName="r" :from="0" :to="airyRadius * i" dur="2s" repeatCount="indefinite" />
                   <animate attributeName="opacity" values="0.6;0" dur="2s" repeatCount="indefinite" />
                </circle>
             </g>
          </g>

          <!-- Lens / Aperture -->
          <rect x="180" y="40" width="10" height="160" fill="#333" rx="5" />
          <rect x="180" :y="120 - aperture/2" width="10" height="aperture" fill="none" stroke="#00d2ff" stroke-width="2" />
          <text x="160" y="30" fill="#888" font-size="8">孔径 D</text>

          <!-- Image Plane (Observation) -->
          <g transform="translate(320, 40)">
             <rect width="60" height="160" fill="#111" stroke="#333" />
             <text x="5" y="-10" fill="#888" font-size="8">成像平面</text>
             
             <!-- Overlapping Intensity Curves -->
             <path :d="intensityPath(-spacing/2)" fill="rgba(255, 215, 0, 0.2)" stroke="#ffd700" />
             <path :d="intensityPath(spacing/2)" fill="rgba(255, 215, 0, 0.2)" stroke="#ffd700" />
             
             <circle cx="30" :cy="80 - spacing*1.5" :r="airyRadius" fill="url(#airy-grad)" opacity="0.8" />
             <circle cx="30" :cy="80 + spacing*1.5" :r="airyRadius" fill="url(#airy-grad)" opacity="0.8" />
          </g>

          <defs>
            <radialGradient id="airy-grad">
              <stop offset="0%" stop-color="#fff" />
              <stop offset="30%" stop-color="#ffd700" />
              <stop offset="100%" stop-color="transparent" />
            </radialGradient>
          </defs>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>是否可分辨: </label>
              <span :style="{ color: canResolve ? '#2ed573' : '#ff4757' }">{{ canResolve ? '可清晰分辨' : '已连成一片' }}</span>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row">
             <label>物体间距</label>
             <el-slider v-model="spacing" :min="2" :max="40" size="mini"></el-slider>
          </div>
          <div class="row">
             <label>孔径大小 D</label>
             <el-slider v-model="aperture" :min="10" :max="80" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>由于波的衍射，一个点光源经过光学系统后不再是点，而是有一定大小的**艾里斑**。当两个点的艾里斑中心间距小于其半径时，它们将无法被区分。这就是瑞利判据。增加镜头口径 $D$ 或减小波长 $\lambda$ 可以有效提高分辨率。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DiffractionLimitViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      spacing: 10,
      aperture: 40
    }
  },
  computed: {
    airyRadius() { return 400 / this.aperture; },
    canResolve() { return this.spacing * 3 > this.airyRadius * 2; },
    intensityPath() {
       return (offset) => {
          let r = this.airyRadius * 2;
          let center = 80 + offset * 1.5;
          let d = `M 0 ${center} `;
          for (let y = -40; y <= 40; y += 2) {
             let val = Math.pow(Math.sin(y/r*Math.PI)/(y/r*Math.PI), 2) * 50;
             if (y === 0) val = 50;
             d += `L ${val} ${center + y} `;
          }
          return d;
       }
    }
  }
}
</script>

<style scoped lang="scss">
.diffraction-limit-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #000; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
